import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { CoinData } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';

interface AddCoinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddCoin: (coinId: string, quantity: number, purchasePrice: number | null) => void;
  cryptoData: CoinData[];
  currency?: string;
}

export default function AddCoinModal({ isOpen, onClose, onAddCoin, cryptoData, currency = 'usd' }: AddCoinModalProps) {
  const [coinId, setCoinId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setCoinId('');
      setQuantity('');
      setPurchasePrice('');
      setSearchQuery('');
      setShowSearchResults(false);
    } else {
      // Focus search input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Filter coins based on search query
  const filteredCoins = useMemo(() => {
    if (!searchQuery.trim()) {
      return cryptoData.slice(0, 50); // Show top 50 when no search
    }
    
    const query = searchQuery.toLowerCase();
    return cryptoData
      .filter(coin => 
        coin.name.toLowerCase().includes(query) ||
        coin.symbol.toLowerCase().includes(query) ||
        coin.id.toLowerCase().includes(query)
      )
      .slice(0, 20); // Limit to 20 results
  }, [cryptoData, searchQuery]);

  // Get selected coin data
  const selectedCoin = useMemo(() => {
    return cryptoData.find(coin => coin.id === coinId);
  }, [cryptoData, coinId]);

  // Handle clicking outside search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSearchResults]);

  const handleCoinSelect = (coin: CoinData) => {
    setCoinId(coin.id);
    setSearchQuery(`${coin.name} (${coin.symbol.toUpperCase()})`);
    setShowSearchResults(false);
    searchInputRef.current?.blur();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const qty = parseFloat(quantity);
    const price = purchasePrice ? parseFloat(purchasePrice) : null;
    
    if (coinId && qty > 0) {
      onAddCoin(coinId, qty, price);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-lg w-[90%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center"
        >
          &times;
        </button>
        <h2 className="text-3xl mb-8 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">✨ Add Coin to Portfolio</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="relative">
            <label htmlFor="coinSearch" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
              💎 Search Cryptocurrency:
            </label>
            <input
              ref={searchInputRef}
              id="coinSearch"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
                if (!e.target.value) {
                  setCoinId('');
                }
              }}
              onFocus={() => setShowSearchResults(true)}
              placeholder="🔍 Search by name, symbol, or ID..."
              className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
            />
            {showSearchResults && filteredCoins.length > 0 && (
              <div
                ref={searchResultsRef}
                className="absolute z-50 w-full mt-2 bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-2xl border-2 border-primary/20 shadow-bubble-lg max-h-80 overflow-y-auto"
              >
                {filteredCoins.map(coin => (
                  <button
                    key={coin.id}
                    type="button"
                    onClick={() => handleCoinSelect(coin)}
                    className={`w-full px-6 py-4 flex items-center justify-between hover:bg-bg transition-colors border-b-2 border-border/50 last:border-b-0 ${
                      coinId === coin.id ? 'bg-primary/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      {coin.image && (
                        <img
                          src={coin.image}
                          alt={coin.name}
                          className="w-10 h-10 rounded-full"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22%3E%3Crect fill=%22%236366f1%22 width=%2240%22 height=%2240%22 rx=%2220%22/%3E%3C/svg%3E';
                          }}
                        />
                      )}
                      <div className="text-left flex-1">
                        <div className="font-bold text-text">{coin.name}</div>
                        <div className="text-sm text-text-secondary">{coin.symbol.toUpperCase()}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-text">{formatCurrency(coin.current_price, currency)}</div>
                      <div className={`text-xs ${(coin.price_change_percentage_24h || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(coin.price_change_percentage_24h || 0) >= 0 ? '+' : ''}
                        {(coin.price_change_percentage_24h || 0).toFixed(2)}%
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {showSearchResults && searchQuery && filteredCoins.length === 0 && (
              <div className="absolute z-50 w-full mt-2 bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-2xl border-2 border-primary/20 shadow-bubble-lg p-6 text-center">
                <p className="text-text-secondary">No coins found matching "{searchQuery}"</p>
              </div>
            )}
            {selectedCoin && (
              <div className="mt-3 p-4 bg-gradient-to-br from-bg to-bg-secondary rounded-2xl border-2 border-primary/20">
                <div className="flex items-center gap-3">
                  {selectedCoin.image && (
                    <img
                      src={selectedCoin.image}
                      alt={selectedCoin.name}
                      className="w-12 h-12 rounded-full"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2248%22 height=%2248%22%3E%3Crect fill=%22%236366f1%22 width=%2248%22 height=%2248%22 rx=%2224%22/%3E%3C/svg%3E';
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-bold text-text">{selectedCoin.name}</div>
                    <div className="text-sm text-text-secondary">{selectedCoin.symbol.toUpperCase()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-text">{formatCurrency(selectedCoin.current_price, currency)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="coinQuantity" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">📊 Quantity:</label>
            <input
              type="number"
              id="coinQuantity"
              step="0.00000001"
              min="0"
              placeholder="0.00"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
            />
          </div>
          <div>
            <label htmlFor="coinPrice" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
              💵 Purchase Price (optional):
            </label>
            <input
              type="number"
              id="coinPrice"
              step="0.01"
              min="0"
              placeholder="Purchase price per coin"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
            />
          </div>
          <button
            type="submit"
            disabled={!coinId || !quantity || parseFloat(quantity) <= 0}
            className="w-full py-4 bg-gradient-to-r from-success to-green-400 border-none rounded-full text-white text-base font-bold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg hover:shadow-glow-green active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ✨ Add to Portfolio
          </button>
        </form>
      </div>
    </div>
  );
}

